import { html, render } from 'lit';
import { ContextProvider } from '@lit/context';
import tourContext from '@common/tour/tour-context.js';
import { driver } from 'driver.js';
import { closeIcon } from '@components/vst-ui-icon/svg/closeIcon.svg.js';
import { captureTourAnalyticsData } from '@common/utils/analyticsHelpers.js';

export const AppTourProvider = Base =>
  class extends Base {
    constructor() {
      super();
      this._tourContextProvider = new ContextProvider(this, {
        context: tourContext,
        initialValue: { tourInProgress: false, showCollectButton: false },
      });
    }

    async startAppTour({ steps, userInitiated, tourButton }) {
      let tourProgressIndex = 0;
      const visitedSteps = new Set();
      let isPreTourAppStateRestored = false;
      const restoreAppState = () => {
        if (isPreTourAppStateRestored) return;
        this._tourContextProvider?.setValue({ tourInProgress: false, showCollectButton: false });
        tourButton.focus();
        tourButton.classList.remove('driver-active-element');
        isPreTourAppStateRestored = true;
        // remove tour stylesheets
        document.querySelectorAll('link[data-tour-stylesheet]').forEach(link => link.remove());
        captureTourAnalyticsData(userInitiated, tourProgressIndex + 1, Array.from(visitedSteps));
      };

      // load tour stylesheets
      await Promise.all(
        ['./driver.css', './tour-popover-styles.css'].map(href => {
          return new Promise((resolve, reject) => {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = href;
            link.dataset.tourStylesheet = '';
            link.onload = () => resolve(link);
            link.onerror = reject;
            document.head.appendChild(link);
            if (link.sheet) resolve(link);
          });
        }),
      );

      const driverTour = driver({
        prevBtnText: 'prev',
        nextBtnText: 'next',
        showProgress: true,
        overlayOpacity: 0.5,
        // (@ejdeposit) These destroy methods don't always both fire when they should,
        // so we  check if we need to restore app state after both
        onDestroyStarted: (element, step, { driver }) => {
          restoreAppState();
          driver.destroy();
        },
        onDestroyed: () => {
          restoreAppState();
        },
        onDeselected: element => {
          element.classList.remove('driver-active-element');
          element.toggleAttribute('inert', false);
        },
        onHighlighted: element => {
          element.toggleAttribute('inert', true);
        },
        onPopoverRender: (popover, { state, driver }) => {
          const { wrapper, closeButton, description, previousButton } = popover;
          const prevWasDisabled = previousButton.disabled;
          description.textContent = '';
          render(steps[state.activeIndex].popover.html(), description);

          const {
            popover: { title },
          } = driver.getActiveStep();
          tourProgressIndex = Math.max(driver.getActiveIndex(), tourProgressIndex);
          visitedSteps.add(title);

          closeButton.textContent = '';
          render(html`${closeIcon}`, closeButton);
          closeButton.addEventListener('click', () => {
            driver.destroy();
          });

          // (brawblan) driver.js has no public API to choose initial popover focus;
          // it focuses the first enabled button in the wrapper (Close by default).
          // Temporarily disable Close and Prev so driver's post-render focus lands
          // on Next, then re-enable them in a microtask (same turn, before paint).
          // Prev restores its prior state.
          closeButton.disabled = true;
          previousButton.disabled = true;
          queueMicrotask(() => {
            closeButton.disabled = false;
            previousButton.disabled = prevWasDisabled;
          });

          const appStyles = window.getComputedStyle(this);
          wrapper.style.setProperty(
            '--bg-color',
            appStyles.getPropertyValue('--vst-color-tour-popover-bg'),
          );
          wrapper.style.setProperty(
            '--fg-color',
            appStyles.getPropertyValue('--vst-color-fg-primary'),
          );
          wrapper.style.setProperty(
            '--brand-color',
            appStyles.getPropertyValue('--vst-color-tour-popover-brand'),
          );
          wrapper.style.setProperty(
            '--vst-font-family',
            appStyles.getPropertyValue('--vst-font-family'),
          );
          wrapper.style.setProperty(
            '--vst-font-size',
            appStyles.getPropertyValue('--vst-font-size-s'),
          );
          wrapper.style.setProperty(
            '--focus-color',
            appStyles.getPropertyValue('--vst-color-text-brand'),
          );
          wrapper.style.setProperty(
            '--button-hover-bg-color',
            appStyles.getPropertyValue('--vst-color-tour-popover-btn-hover-bg'),
          );
          wrapper.style.setProperty(
            '--close-button-hover-bg-color',
            appStyles.getPropertyValue('--vst-color-tour-popover-close-btn-hover-bg'),
          );
          wrapper.style.setProperty(
            '--close-icon-hover-fill',
            appStyles.getPropertyValue('--vst-color-tour-popover-close-icon-hover-fill'),
          );
          wrapper.style.setProperty(
            '--vst-space-3xs',
            appStyles.getPropertyValue('--vst-space-3xs'),
          );
          wrapper.style.setProperty(
            '--vst-space-2xs',
            appStyles.getPropertyValue('--vst-space-2xs'),
          );
          wrapper.style.setProperty('--vst-space-xs', appStyles.getPropertyValue('--vst-space-xs'));
          wrapper.style.setProperty('--vst-space-m', appStyles.getPropertyValue('--vst-space-m'));
          wrapper.style.setProperty('--vst-space-l', appStyles.getPropertyValue('--vst-space-l'));
          wrapper.style.setProperty('--vst-radius', appStyles.getPropertyValue('--vst-radius'));
        },
        steps,
      });

      driverTour.drive();
    }

    readyAppForTour({ showCollectButton = false } = {}) {
      localStorage.setItem('tour-seen', true);
      this._tourContextProvider.setValue({ tourInProgress: true, showCollectButton });
    }
  };
